import { useApolloClient } from "@apollo/client";
import { JwtPayload, jwtDecode } from "jwt-decode";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";

import { JWT_KEY } from "~/config/constants";

import useRefreshTokenQuery from "../api/useRefreshTokenQuery";
import AuthStatusNotifier from "../util/AuthStatusNotifier";
import AuthContext from "./AuthContext";

export default function AuthContextProvider({ children }: PropsWithChildren<unknown>) {
    const client = useApolloClient();

    const [authState, setAuthState] = useState<AuthState>({
        type: "initializing",
    });

    const { refresh } = useRefreshTokenQuery();

    const onLogin = useCallback(async (jwt: string) => {
        const data = jwtDecode(jwt) as JwtPayload & { id: string };
        localStorage.setItem(JWT_KEY, jwt);
        setAuthState({
            type: "loggedIn",
            userId: data.id,
        });
    }, []);

    const onLogout = useCallback(async () => {
        client.clearStore();
        localStorage.clear();
        setAuthState({ type: "loggedOut" });
    }, [client]);

    // Initializing auth status
    useEffect(() => {
        if (authState.type !== "initializing") return;
        const jwt = localStorage.getItem(JWT_KEY);
        if (jwt) {
            onLogin(jwt);
        } else {
            onLogout();
        }
    }, [authState, onLogin, onLogout]);

    // Listen for auth errors in apollo
    useEffect(() => {
        AuthStatusNotifier.subscribe(async () => {
            try {
                const refreshResult = await refresh();

                if (!refreshResult.data?.userRefreshSession?.jwt) {
                    await onLogout();
                    return;
                }

                onLogin(refreshResult.data.userRefreshSession.jwt);
                return refreshResult.data.userRefreshSession.jwt;
            } catch {
                await onLogout();
                return;
            }
        });
        return () => {
            AuthStatusNotifier.unsubscribe();
        };
    }, [refresh, onLogout, onLogin]);

    return (
        <AuthContext.Provider
            value={{
                state: authState.type,
                userId: authState.type === "loggedIn" ? authState.userId : undefined,
                isLoggedIn: authState.type === "loggedIn",
                onLogin,
                onLogout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
